/* biome-ignore-all lint/suspicious/noExplicitAny: legacy data structure */
import type React from "react"
import { Tooltip } from "antd"
import dayjs from "dayjs"

interface SrsLogItem {
  id: number
  rating: number
  is_correct?: boolean | number | null
  practice_mode?: string
  response_time_ms?: number | null
  previous_state?: number
  next_state?: number
  next_scheduled_days?: number
  next_due?: string | Date
  reviewed_at: string | Date
}

interface SrsProgressData {
  id?: number
  state?: number
  stability?: number
  difficulty?: number
  due?: string | Date
  review_count?: number
  lapse_count?: number
  last_rating?: number | null
  recent_logs?: SrsLogItem[]
}

interface SrsFormBadgesProps {
  srsProgress?: SrsProgressData | null
  maxItems?: number
}

const RATING_CONFIG: Record<
  number,
  { label: string; text: string; bgClass: string; borderClass: string }
> = {
  1: {
    label: "Again (Học lại)",
    text: "1",
    bgClass: "bg-red-500 text-white hover:bg-red-600",
    borderClass: "border-red-600",
  },
  2: {
    label: "Hard (Khó)",
    text: "2",
    bgClass: "bg-amber-500 text-white hover:bg-amber-600",
    borderClass: "border-amber-600",
  },
  3: {
    label: "Good (Tốt)",
    text: "3",
    bgClass: "bg-emerald-500 text-white hover:bg-emerald-600",
    borderClass: "border-emerald-600",
  },
  4: {
    label: "Easy (Dễ)",
    text: "4",
    bgClass: "bg-blue-500 text-white hover:bg-blue-600",
    borderClass: "border-blue-600",
  },
}

export const SrsFormBadges: React.FC<SrsFormBadgesProps> = ({
  srsProgress,
  maxItems = 5,
}) => {
  if (!srsProgress) {
    return <span className="text-gray-300 text-xs italic">—</span>
  }

  const logs = (srsProgress.recent_logs || []).slice(-maxItems)

  if (logs.length === 0) {
    return <span className="text-gray-400 text-xs italic">Chưa ôn tập</span>
  }

  return (
    <div className="flex items-center justify-center gap-1">
      {logs.map((log, index) => {
        const ratingInfo = RATING_CONFIG[log.rating] || {
          label: `Rating ${log.rating}`,
          text: `${log.rating}`,
          bgClass: "bg-gray-400 text-white",
          borderClass: "border-gray-500",
        }

        const practiceModeLabel =
          log.practice_mode === "flashcard"
            ? "Flashcard"
            : log.practice_mode === "quiz_multiple_choice"
              ? "Trắc nghiệm"
              : log.practice_mode || "Flashcard"

        const tooltipContent = (
          <div className="flex flex-col gap-0.5 text-xs p-0.5">
            <div className="font-bold border-b border-gray-600 pb-1 mb-1">
              Lần ôn #{logs.length - index} ({ratingInfo.label})
            </div>
            <div>
              📅 <strong>Thời gian:</strong>{" "}
              {dayjs(log.reviewed_at).format("DD/MM/YYYY HH:mm")}
            </div>
            <div>
              ⚡ <strong>Chế độ:</strong> {practiceModeLabel}
              {log.is_correct !== undefined && log.is_correct !== null && (
                <span>
                  {" "}
                  ({log.is_correct === 1 || log.is_correct === true ? "✅ Đúng" : "❌ Sai"})
                </span>
              )}
            </div>
            {log.response_time_ms ? (
              <div>
                ⏱️ <strong>Tốc độ:</strong> {(log.response_time_ms / 1000).toFixed(1)}s
              </div>
            ) : null}
            {log.next_due ? (
              <div>
                ⏰ <strong>Hạn tiếp theo:</strong>{" "}
                {dayjs(log.next_due).format("DD/MM/YYYY")}
                {log.next_scheduled_days !== undefined && ` (+${log.next_scheduled_days} ngày)`}
              </div>
            ) : null}
          </div>
        )

        return (
          <Tooltip key={log.id || index} title={tooltipContent} placement="top">
            <div
              className={`w-5 h-5 rounded-full flex items-center justify-center font-bold text-[10px] shadow-xs cursor-pointer transition-transform hover:scale-110 ${ratingInfo.bgClass}`}
            >
              {ratingInfo.text}
            </div>
          </Tooltip>
        )
      })}
    </div>
  )
}

export default SrsFormBadges
